import { useState, useRef, useEffect } from "react";
import { useFetcher, useLocation } from "@remix-run/react";
import { MessageSquarePlus, X, Send } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";

const AUTH_PATH_PREFIXES = [
  "/login",
  "/signup",
  "/resetpassword",
  "/verifyemail",
  "/confirmpassword",
  "/auth/",
];

function isAuthRoute(pathname: string): boolean {
  return AUTH_PATH_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p),
  );
}

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const fetcher = useFetcher<{ ok?: boolean; error?: string }>();
  const formRef = useRef<HTMLFormElement>(null);
  const pendingRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    if (fetcher.state === "submitting") {
      pendingRef.current = true;
    }
    if (fetcher.state === "idle" && pendingRef.current && fetcher.data) {
      pendingRef.current = false;
      if (fetcher.data.ok) {
        toast({ title: "Thanks for your feedback!" });
        formRef.current?.reset();
        setOpen(false);
      } else {
        toast({
          title: "Something went wrong",
          description: fetcher.data.error ?? "Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [fetcher.state, fetcher.data, toast]);

  if (isAuthRoute(location.pathname)) {
    return null;
  }

  if (open) {
    return (
      <div className="fixed bottom-6 right-6 z-50 w-[340px] rounded-xl border border-gray-200 bg-white shadow-2xl animate-in slide-in-from-bottom-4 fade-in duration-200">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <span className="text-sm font-semibold text-gray-900">
            Give feedback
          </span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          ref={formRef}
          className="space-y-3 px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            fd.set("page", location.pathname + location.search);
            fetcher.submit(fd, {
              method: "post",
              action: "/api/feedback",
            });
          }}
        >
          <div>
            <Label htmlFor="fb-trying" className="text-xs text-gray-600">
              What were you trying to do?
            </Label>
            <Textarea
              id="fb-trying"
              name="trying"
              required
              rows={2}
              maxLength={2000}
              placeholder="e.g. I wanted to find a store near me…"
              className="mt-1 resize-none text-sm focus-visible:border-orange-300 focus-visible:ring-2 focus-visible:ring-orange-500/25 focus-visible:ring-offset-0"
            />
          </div>

          <div>
            <Label htmlFor="fb-wrong" className="text-xs text-gray-600">
              What went wrong?{" "}
              <span className="text-gray-400">(optional)</span>
            </Label>
            <Textarea
              id="fb-wrong"
              name="wrong"
              rows={2}
              maxLength={2000}
              placeholder="e.g. Page didn't load, couldn't check out…"
              className="mt-1 resize-none text-sm focus-visible:border-orange-300 focus-visible:ring-2 focus-visible:ring-orange-500/25 focus-visible:ring-offset-0"
            />
          </div>

          <div>
            <Label htmlFor="fb-email" className="text-xs text-gray-600">
              Email <span className="text-gray-400">(optional)</span>
            </Label>
            <Input
              id="fb-email"
              name="email"
              type="email"
              maxLength={320}
              placeholder="you@example.com"
              className="mt-1 text-sm focus-visible:border-orange-300 focus-visible:ring-2 focus-visible:ring-orange-500/25 focus-visible:ring-offset-0"
            />
          </div>

          <button
            type="submit"
            disabled={fetcher.state !== "idle"}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" />
            {fetcher.state !== "idle" ? "Sending…" : "Send feedback"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-orange-600 px-4 py-2.5 text-sm font-medium text-white shadow-lg transition-all hover:bg-orange-700 hover:shadow-xl active:scale-95"
    >
      <MessageSquarePlus className="h-4 w-4" />
      Feedback
    </button>
  );
}
